import type { AxiosInstance, AxiosResponse } from "axios";
import type { RootResponse } from "@/models/rootResponse";
import type {
   GradedSurveyApiResponse,
   GradedSurveyBase,
   GradedSurveyEdit,
   SurveyBase,
   SurveyEdit,
} from "@/models/survey";

type GetGradedSurvey = { gradedSurveyId: string };

type GetGradedSurveyEdit = { surveyId: string };

type GetGradedSurveyTaking = { surveyId: string };

type UpdateGradedSurvey = {
   surveyId: string;
   gradedSurveyId: string;
   data: FormData | Record<string, unknown>;
};

class GradedSurveyService {
   private provider: AxiosInstance;

   constructor(provider: AxiosInstance) {
      this.provider = provider;
   }

   async createGradedSurvey(
      data: FormData | Record<string, unknown>,
   ): Promise<SurveyBase["id"]> {
      const formData = new FormData();
      formData.append("mutate[0][operation]", "create");
      formData.append("mutate[0][relations][survey][operation]", "create");

      const isGradedAttribute = (key: string) =>
         key === "timer" ||
         key === "total_grade" ||
         key === "has_results_showed";

      const entries: Array<[string, FormDataEntryValue | string]> =
         data instanceof FormData
            ? Array.from(data.entries())
            : Object.entries(data).map(([k, v]) => [k, String(v)]);

      for (const [key, value] of entries) {
         if (isGradedAttribute(key)) {
            formData.append(`mutate[0][attributes][${key}]`, value as string);
         } else {
            formData.append(
               `mutate[0][relations][survey][attributes][${key}]`,
               value as FormDataEntryValue,
            );
         }
      }

      const response: AxiosResponse<{ created: string[] }> =
         await this.provider.post(`rest/graded-surveys/mutate`, formData, {
            headers: {
               "Content-Type": "multipart/form-data",
            },
         });

      return response.data.created[0];
   }

   async getGradedSurvey({
      gradedSurveyId,
   }: GetGradedSurvey): Promise<GradedSurveyEdit> {
      const response: AxiosResponse<
         RootResponse<GradedSurveyApiResponse<SurveyEdit>[]>
      > = await this.provider.post(`rest/graded-surveys/search`, {
         search: {
            filters: [
               {
                  field: "id",
                  value: gradedSurveyId,
               },
            ],
            includes: [
               {
                  relation: "survey",
                  includes: [
                     {
                        relation: "status",
                     },
                     {
                        relation: "type",
                     },
                     {
                        relation: "sites",
                     },
                     {
                        relation: "media",
                     },
                     {
                        relation: "updatedBy",
                     },
                     {
                        relation: "pages",
                        sorts: [{ field: "order", direction: "asc" }],
                        includes: [
                           {
                              relation: "questions",
                              sorts: [{ field: "order", direction: "asc" }],
                              includes: [
                                 {
                                    relation: "questionType",
                                 },
                              ],
                           },
                        ],
                     },
                  ],
               },
            ],
         },
      });

      const { id, ...data } = response.data.data[0];
      const survey = {
         ...data,
         ...data.survey,
         gradedId: id,
      };

      return survey;
   }

   async getGradedSurveyEdit({
      surveyId,
   }: GetGradedSurveyEdit): Promise<GradedSurveyEdit> {
      const response: AxiosResponse<
         RootResponse<GradedSurveyApiResponse<SurveyEdit>[]>
      > = await this.provider.post(`rest/graded-surveys/search`, {
         search: {
            gates: ["view", "update", "delete"],
            filters: [
               {
                  field: "survey.id",
                  value: surveyId,
               },
            ],
            includes: [
               {
                  relation: "survey",
                  includes: [
                     {
                        relation: "status",
                     },
                     {
                        relation: "type",
                     },
                     {
                        relation: "sites",
                     },
                     {
                        relation: "media",
                     },
                     {
                        relation: "updatedBy",
                     },
                     {
                        relation: "pages",
                        sorts: [{ field: "order", direction: "asc" }],
                        includes: [
                           {
                              relation: "questions",
                              sorts: [{ field: "order", direction: "asc" }],
                              includes: [
                                 {
                                    relation: "linkedChoices",
                                    sorts: [
                                       { field: "order", direction: "asc" },
                                    ],
                                    includes: [
                                       {
                                          relation: "media",
                                       },
                                       {
                                          relation: 'comment',
                                       },
                                    ],
                                 },
                                 {
                                    relation: "questionType",
                                 },
                                 {
                                    relation: "media",
                                 },
                              ],
                           },
                        ],
                     },
                  ],
                  aggregates: [
                     {
                        relation: "entries",
                        type: "count",
                        alias: "entries_count",
                     },
                  ],
               },
            ],
         },
      });

      const { id, ...data } = response.data.data[0];
      const survey = {
         ...data,
         ...data.survey,
         gradedId: id,
      };

      return survey;
   }

   async getGradedSurveyTaking({
      surveyId,
   }: GetGradedSurveyTaking): Promise<GradedSurveyBase> {
      const response: AxiosResponse<
         RootResponse<GradedSurveyApiResponse<SurveyEdit>[]>
      > = await this.provider.post(`rest/graded-surveys/search`, {
         search: {
            filters: [
               {
                  field: "survey.id",
                  value: surveyId,
               },
            ],
         },
      });

      return response.data.data[0];
   }

   async updateGradedSurvey({
      data,
      surveyId,
      gradedSurveyId,
   }: UpdateGradedSurvey): Promise<void> {
      const formData = new FormData();
      formData.append("mutate[0][operation]", "update");
      formData.append("mutate[0][key]", gradedSurveyId);
      formData.append("mutate[0][relations][survey][operation]", "update");
      formData.append("mutate[0][relations][survey][key]", surveyId);

      const isGradedAttribute = (key: string) =>
         key === "timer" ||
         key === "total_grade" ||
         key === "has_results_showed";

      const entries: Array<[string, FormDataEntryValue | string]> =
         data instanceof FormData
            ? Array.from(data.entries())
            : Object.entries(data).map(([k, v]) => [k, String(v)]);

      for (const [key, value] of entries) {
         if (isGradedAttribute(key)) {
            formData.append(`mutate[0][attributes][${key}]`, value as string);
         } else {
            formData.append(
               `mutate[0][relations][survey][attributes][${key}]`,
               value as FormDataEntryValue,
            );
         }
      }

      await this.provider.post(`rest/graded-surveys/mutate`, formData, {
         headers: {
            "Content-Type": "multipart/form-data",
         },
      });
   }
}

export default GradedSurveyService;
